import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import { apiCall } from "../utils/apiService";
import { getPlanFeatures } from "../utils/planFeatures";
import PlanLockedBanner from "./PlanLockedBanner";
import "./message/ApiTestPage.css";

const APIAccessLayer = () => {
  const [apiKey, setApiKey] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [spinner, setSpinner] = useState(false);
  const [copied, setCopied] = useState(false);
  const [apiAllowed, setApiAllowed] = useState(true);

  const fetchAPIToken = async () => {
    try {
      const response = await apiCall({
        url: `user/get-user-by-token`,
        method: "GET",
      });
      if (response?.success && response.data) {
        localStorage.setItem("user", JSON.stringify(response.data));
        setApiKey(response.data.api || "");
        setApiAllowed(getPlanFeatures(response.data).api);
      }
    } catch (error) {
      console.error("Error fetching API token:", error);
    }
  };

  const hideAlert = (sec = 1) => {
    setTimeout(() => {
      setAlertMessage("");
      setSpinner(false);
    }, sec * 1000);
  };

  const handleSubmit = async () => {
    if (!apiAllowed) {
      setAlertMessage({
        message: "API access is not included in your plan.",
        class: "danger",
      });
      hideAlert(3);
      return;
    }
    setSpinner(true);
    try {
      const response = await apiCall({
        url: `user/generate-API`,
        method: "GET",
      });

      setAlertMessage({
        message: response.msg,
        class: response.success ? "success" : "danger",
      });

      if (response.success) {
        localStorage.setItem("apiKey", response.token);
        setApiKey(response.token);
        hideAlert(2);
      } else {
        hideAlert(3);
      }
    } catch (error) {
      setAlertMessage({ message: error.message, class: "danger" });
      hideAlert(2);
    }
  };

  const copyKey = async () => {
    if (!apiKey) return;
    try {
      await navigator.clipboard.writeText(apiKey);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    document.title = "API Keys";
    fetchAPIToken();
  }, []);

  const sampleUrl =
    "https://wap.alphaxcloud.com/api/send-message/chat?to=YOUR_NUMBER&text=YOUR_MESSAGE&token=YOUR_API_KEY&client_id=YOUR_CLIENT_ID";

  return (
    <div className="api-test-page">
      {!apiAllowed ? (
        <PlanLockedBanner
          title="API access not in your plan"
          message="Generate keys and use the Send API after upgrading to a plan with API access."
        />
      ) : null}
      <div className="api-test-grid" style={{ gridTemplateColumns: "1.1fr 1fr" }}>
        <div className="api-test-card">
          <div className="api-test-card-head">
            <h6>Your API key</h6>
            <span>Bearer token</span>
          </div>
          <div className="api-test-card-body">
            <div className="api-test-note">
              <Icon icon="mdi:shield-key-outline" width={18} />
              <span>
                Keep this key private. Regenerating invalidates the previous token used in
                integrations.
              </span>
            </div>

            {alertMessage && (
              <div className={`api-test-alert ${alertMessage.class}`}>{alertMessage.message}</div>
            )}

            <label className="api-test-label">Current key</label>
            <div className="d-flex gap-2 align-items-center">
              <input
                type="text"
                className="form-control"
                readOnly
                value={apiKey || "No key generated yet"}
              />
              <button
                type="button"
                className="btn btn-outline-success"
                onClick={copyKey}
                disabled={!apiKey}
                title="Copy"
              >
                <Icon icon={copied ? "mdi:check" : "mdi:content-copy"} width={18} />
              </button>
            </div>

            <div className="api-test-actions">
              <button
                type="button"
                className="btn api-test-submit"
                onClick={handleSubmit}
                disabled={!apiAllowed || spinner}
              >
                {spinner ? "Generating…" : "Generate new key"}
              </button>
            </div>
          </div>
        </div>

        <div className="api-test-card">
          <div className="api-test-card-head">
            <h6>Quick start</h6>
            <span>GET example</span>
          </div>
          <div className="api-test-card-body">
            <p className="api-test-help mb-3" style={{ fontSize: "0.85rem" }}>
              Send a text message with a simple GET request. Replace placeholders with your
              values.
            </p>
            <pre
              className="code-samples-block"
              style={{
                margin: 0,
                padding: 14,
                borderRadius: 12,
                background: "#0f172a",
                color: "#e2e8f0",
                fontSize: 12,
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              <code>{sampleUrl}</code>
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
};

export default APIAccessLayer;
